const API_ROOT = process.env.REACT_APP_API_ROOT || "";

async function getJson(path) {
  const res = await fetch(`${API_ROOT}${path}`);
  if (!res.ok) throw new Error(`${path} ${res.status}`);
  return res.json();
}

// POST with an optional JSON body. Non-2xx surfaces the server's text body in
// the thrown error so backend validation messages reach the UI banner. The
// thrown Error also carries `status` and the parsed `body` (string or object)
// so callers can build a user-facing message via `friendlyApiErrorMessage`.
async function postJson(path, body) {
  const init = { method: "POST" };
  if (body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_ROOT}${path}`, init);
  if (!res.ok) {
    const rawText = await res.text().catch(() => "");
    let parsedBody = rawText;
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json") || contentType.includes("application/problem+json")) {
      try {
        parsedBody = JSON.parse(rawText);
      } catch {
        parsedBody = rawText;
      }
    }
    const err = new Error(`${path} ${res.status}${rawText ? `: ${rawText}` : ""}`);
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  return res.json();
}

// Pulls a user-facing message out of an error from `postJson` / `fetch`.
// Network failures (no `status` on the error) return a connectivity note;
// otherwise the server's text body is surfaced verbatim. ValidationProblem
// JSON bodies ({ title, errors: { field: [msg] } }) flatten into
// "title — field: msg; field: msg".
export function apiErrorMessage(error) {
  if (!error || typeof error !== "object" || !("status" in error)) {
    return "Could not reach the server. Check your internet connection and try again.";
  }

  const { body } = error;
  if (typeof body === "string" && body.trim()) return body.trim();
  if (body && typeof body === "object") {
    const parts = [];
    if (body.errors && typeof body.errors === "object") {
      for (const [field, messages] of Object.entries(body.errors)) {
        const list = Array.isArray(messages) ? messages : [messages];
        for (const message of list) {
          parts.push(field && field !== "$" ? `${field}: ${message}` : String(message));
        }
      }
    }
    if (parts.length > 0) {
      return body.title ? `${body.title} — ${parts.join("; ")}` : parts.join("; ");
    }
    if (typeof body.detail === "string" && body.detail.trim()) return body.detail.trim();
    if (typeof body.title === "string" && body.title.trim()) return body.title.trim();
    if (typeof body.message === "string" && body.message.trim()) return body.message.trim();
  }

  return `The server returned an error (status ${error.status}).`;
}

// POST /api/otp/request — server decrypts the token, sends a 6-digit code to that email.
// Returns { sent: true } on success or throws with { reason, retryAfterSeconds } on 429.
export async function requestOtp(token) {
  const res = await fetch(`${API_ROOT}/otp/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token })
  });
  if (res.status === 429) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.reason || "throttled");
    err.reason = data.reason;
    err.retryAfterSeconds = data.retryAfterSeconds || 30;
    throw err;
  }
  if (!res.ok) throw new Error(`requestOtp ${res.status}`);
  return res.json();
}

// POST /api/otp/verify — returns { verified: true|false }.
export async function verifyOtp(token, code) {
  const res = await fetch(`${API_ROOT}/otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, code })
  });
  if (!res.ok) throw new Error(`verifyOtp ${res.status}`);
  const data = await res.json();
  return data.verified === true;
}

// A selection is { kind: "group", groupId } or { kind: "parent", parentCode }.
// `selectionKey` produces a stable string for cache maps and React keys.
export function selectionKey(selection) {
  if (!selection) return "";
  return selection.kind === "group" ? `group:${selection.groupId}` : `parent:${selection.parentCode}`;
}

// Serializes a selection into the `groupId=…` xor `parentAgencyCode=…` (or
// `parentJurisdictionCode=…`) query fragment used by every group-aware endpoint.
function selectionToQuery(selection, parentParamName = "parentAgencyCode") {
  if (selection.kind === "group") return `groupId=${encodeURIComponent(selection.groupId)}`;
  return `${parentParamName}=${encodeURIComponent(selection.parentCode)}`;
}

// Collapses the flat row-per-(parent, subagency) shape into:
//   options:                flat list of dropdown choices (groups + ungrouped parents),
//                           each carrying its `selection`, full `label`, and `address`.
//   subagenciesBySelection: { [selectionKey]: [subagency, ...] } deduped by code.
// Rows with non-null GROUP_ID collapse into a single group option; rows with null
// GROUP_ID stay individual parent options.
function reshapeJurisdictionRows(rows) {
  const optionMap = new Map();
  const subsBySelection = {};
  const seenSubKeys = new Set();

  for (const row of rows) {
    const groupId = row.GROUP_ID;
    const parentCode = row.PARENT_JURISDICTION_CODE;
    const isGroup = groupId != null;
    const selection = isGroup ? { kind: "group", groupId } : { kind: "parent", parentCode };
    const key = selectionKey(selection);

    if (!optionMap.has(key)) {
      optionMap.set(key, {
        key,
        selection,
        name: row.PARENT_JURISDICTION_NAME || (isGroup ? `Group ${groupId}` : parentCode),
        address: row.PARENT_AGENCY_ADDRESS || "",
        memberCodes: []
      });
      subsBySelection[key] = [];
    }
    const option = optionMap.get(key);
    if (parentCode && !option.memberCodes.includes(parentCode)) {
      option.memberCodes.push(parentCode);
    }

    if (row.JURISDICTION_CODE) {
      const dedupeKey = `${key}|${row.JURISDICTION_CODE}`;
      if (!seenSubKeys.has(dedupeKey)) {
        seenSubKeys.add(dedupeKey);
        subsBySelection[key].push({
          code: row.JURISDICTION_CODE,
          name: row.JURISDICTION_NAME,
          address: row.AGENCY_ADDRESS || "",
          phone: row.CONTACT_BUSINESS_TELEPHONE || "",
          status: row.JURISDICTION_STATUS || "",
          originalParentCode: row.ORIGINAL_PARENT_CODE || parentCode,
          groupName: row.GROUP_NAME || ""
        });
      }
    }
  }

  // Bake the final display label after all member codes are known.
  for (const option of optionMap.values()) {
    option.memberCodes.sort((a, b) => a.localeCompare(b)); // sort parent agency codes
    option.label = option.memberCodes.length
      ? `${option.name} (${option.memberCodes.join(", ")})`
      : option.name;
  }

  // sort subagency names
  for (const list of Object.values(subsBySelection)) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  // sort group names
  const options = [...optionMap.values()].sort((a, b) => a.name.localeCompare(b.name));
  return { options, subagenciesBySelection: subsBySelection };
}

// RM_JURISDICTIONS_WITH_DISTRICTS_WITH_FUNDING_AGENCIES
// Filtered server-side by the caller's read_write access — token is required.
export async function fetchJurisdictions(token) {
  if (!token) throw new Error("fetchJurisdictions: token is required");
  const rows = await getJson(`/rm/jurisdictions?token=${encodeURIComponent(token)}`);
  return reshapeJurisdictionRows(rows);
}

// RM_JURISDICTION_CONTACTS
export async function fetchJurisdictionContacts(subagencyCode) {
  const rows = await getJson(`/rm/jurisdictions/${encodeURIComponent(subagencyCode)}/contacts`);
  return rows.map((r) => ({
    name: r.CONTACT_NAME,
    type: r.CONTACT_TYPE,
    phone: r.CONTACT_BUSINESS_TELEPHONE || "",
    email: r.CONTACT_EMAIL || "",
    address: r.CONTACT_ADDRESS || "",
    status: r.JURISDICTION_STATUS || ""
  }));
}

export function formatPhoneDigits(digits) {
  const trimmed = String(digits || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  if (trimmed.length === 0) return "";
  if (trimmed.length < 4) return `(${trimmed}`;
  if (trimmed.length < 7) return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
}

// DB stores phone as "<10-digit phone in any format>" optionally followed by
// " EXT " / " ext " and the extension digits.
export function parsePhoneAndExtension(raw) {
  if (!raw) return { phone: "", phoneExtension: "" };
  const [mainPart, extensionPart = ""] = String(raw).split(/ ext /i);
  return {
    phone: mainPart.replace(/\D/g, "").slice(0, 10),
    phoneExtension: extensionPart.replace(/\D/g, "").slice(0, 10)
  };
}

export function formatPhoneWithExtension(phone, phoneExtension) {
  const phoneDigits = String(phone || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  const extensionDigits = String(phoneExtension || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  const formattedPhone = formatPhoneDigits(phoneDigits);
  if (!formattedPhone) return extensionDigits ? `EXT ${extensionDigits}` : "";
  return extensionDigits ? `${formattedPhone} EXT ${extensionDigits}` : formattedPhone;
}

// AUTHORIZED_SIGNER — returns the array of signers for a selection (group or
// single parent). For a group, the API returns a single group-level signer.
// `agreementRequired` is sourced server-side from GROUP_AGREEMENT_REQUIRED for
// groups and AGREEMENT_REQ for parents; "Y" → true, anything else → false.
export async function fetchAuthorizedSigners(selection) {
  const rows = await getJson(`/rm/authorized-signers?${selectionToQuery(selection)}`);
  return rows.map((r) => {
    const { phone, phoneExtension } = parsePhoneAndExtension(r.AUTH_PHONE);
    return {
      // Null for group-synthetic rows (the row lives on PARENT_JURISDICTION_GROUP,
      // not AUTHORIZED_SIGNER). Round-tripped on submit so the API can update the
      // AUTHORIZED_SIGNER row by primary key.
      id: r.ID ?? null,
      name: r.AUTH_NAME || "",
      title: r.AUTH_TITLE || "",
      phone,
      phoneExtension,
      email: r.AUTH_EMAIL || "",
      agreementName: r.AGREEMENT_NAME || "",
      agreementType: r.AGREEMENT_TYPE || "",
      agreementRequired: r.AGREEMENT_REQ === "Y"
    };
  });
}

// POST submission. The server generates SubmissionIdentifier + SubmittedAt
// and returns both, plus emailStatus for the confirmation email send result.
export function submitPreliminaryNotification(payload) {
  return postJson("/rm/preliminary-notification", payload);
}

// Resends the confirmation email for an already-saved submission, without
// resubmitting the form. The server reattaches the PDF stored on the submission
// row, so no payload is needed. Returns { emailStatus: "sent" | "failed" | "no-recipients" }.
export function resendConfirmationEmail(submissionIdentifier) {
  return postJson(
    `/rm/preliminary-notification/${encodeURIComponent(submissionIdentifier)}/resend-confirmation`
  );
}

// Latest submission for a selection, or null if none exists. The API returns
// 204 No Content when there is no submission matching the selection.
export async function fetchLatestPreliminaryNotification(selection) {
  if (!selection) return null;
  const path = `/rm/preliminary-notification/latest?${selectionToQuery(selection, "parentJurisdictionCode")}`;
  const res = await fetch(`${API_ROOT}${path}`);
  if (!res.ok || res.status === 204) return null;
  return res.json();
}

// Latest submission for a selection that contains the given subagency, or null
// if none exists. Returns 204 No Content when no submission matches.
export async function fetchLatestPreliminaryNotificationBySubagency(selection, subagencyJurisdictionCode) {
  if (!selection || !subagencyJurisdictionCode) return null;
  const path = `/rm/preliminary-notification/latest-by-subagency?${selectionToQuery(selection, "parentJurisdictionCode")}&subagencyJurisdictionCode=${encodeURIComponent(subagencyJurisdictionCode)}`;
  const res = await fetch(`${API_ROOT}${path}`);
  if (!res.ok || res.status === 204) return null;
  return res.json();
}

// "1,500" → 1500. Empty/invalid → 0 (server will 400 on the Range validator if zero is invalid).
function parseIntegerInput(raw) {
  if (raw == null || raw === "") return 0;
  const cleaned = String(raw).replace(/[,\s]/g, "");
  const value = parseInt(cleaned, 10);
  return Number.isFinite(value) ? value : 0;
}

// "$250,000.00" → 250000. Empty/invalid → 0.
function parseDecimalInput(raw) {
  if (raw == null || raw === "") return 0;
  const cleaned = String(raw).replace(/[$,\s]/g, "");
  const value = parseFloat(cleaned);
  return Number.isFinite(value) ? value : 0;
}

// "" → null, otherwise passes through the YYYY-MM-DD string. The form's DateField
// already yields this shape; null maps to SQL NULL via the DateOnly? DTO field.
function toDateOrNull(raw) {
  return raw ? raw : null;
}

// "Yes" / "No" → bool. Anything else defaults to false.
function yesNoToBool(raw) {
  return raw === "Yes";
}

// Builds the PreliminaryFormSubmissionRequestDto the API expects from the
// form's state. Only entries with a selected subagency are included.
export function buildSubmissionPayload({
  userEmail,
  selection,
  signers,
  preparer,
  confirmRecipients,
  entries,
  pdfBase64
}) {
  const subagencies = entries
    .filter((entry) => entry.subagency)
    .map((entry) => ({
      JurisdictionCode: entry.subagency,
      AccountType: entry.accountType,
      IsAccountActive: yesNoToBool(entry.active),
      NewTaxBillDescription: entry.reqDescUpdate ? entry.newDescription || null : null,
      ChargeStatus: entry.chargeStatus,
      ChargeCategory: entry.chargeCategory,
      ServiceDescription: entry.serviceDesc,
      EstimatedParcels: parseIntegerInput(entry.estParcels),
      EstimatedAmount: parseDecimalInput(entry.estAmount),
      StatuteCode: entry.enablingStatute,
      LegalAuthorityType: entry.legalAuthorityType,
      ApprovalMethod: entry.approvalMethod,
      ElectionDate: toDateOrNull(entry.electionDate),
      ApprovalDate: toDateOrNull(entry.approvalDate),
      AdoptionType: entry.adoptionType || null,
      AdoptionDate: toDateOrNull(entry.adoptionDate),
      OriginalAdoptionDate: toDateOrNull(entry.origAdoptionDate),
      IsLegalAuthorityCurrent: yesNoToBool(entry.legalInPlace)
    }));

  const signerList = Array.isArray(signers) ? signers : [];

  return {
    SubmittedByEmail: userEmail,
    GroupId: selection?.kind === "group" ? selection.groupId : null,
    ParentJurisdictionCode: selection?.kind === "parent" ? selection.parentCode : null,
    FormPreparerFirstName: preparer.first,
    FormPreparerLastName: preparer.last,
    FormPreparerPhone: formatPhoneWithExtension(preparer.phone, preparer.phoneExtension),
    FormPreparerEmail: preparer.email,
    CcFormPreparer: !!confirmRecipients.preparer,
    CcContacts: !!confirmRecipients.contacts,
    CcAuthorizedSigner: !!confirmRecipients.signer,
    Subagencies: subagencies,
    Signers: signerList.map((s) => ({
      AuthorizedSignerId: s.id ?? null,
      Name: s.name || null,
      Title: s.title || null,
      Phone: formatPhoneWithExtension(s.phone, s.phoneExtension) || null,
      Email: s.email || null
    })),
    SubmissionPdfBase64: pdfBase64 || null
  };
}

// Maps a subagency record from a prior submission (server DTO shape) to the
// form's entry-field shape. Used to prefill SubagencyCard from the latest
// submission for the same subagency.
export function entryFieldsFromPriorSubagency(prior) {
  return {
    accountType: prior.accountType || "",
    active: prior.isAccountActive ? "Yes" : "No",
    chargeStatus: prior.chargeStatus || "",
    chargeCategory: prior.chargeCategory || "",
    serviceDesc: prior.serviceDescription || "",
    estParcels: prior.estimatedParcels != null ? String(prior.estimatedParcels) : "",
    estAmount: prior.estimatedAmount != null ? String(prior.estimatedAmount) : "",
    enablingStatute: prior.statuteCode || "",
    legalAuthorityType: prior.legalAuthorityType || "",
    approvalMethod: prior.approvalMethod || "",
    electionDate: prior.electionDate || "",
    approvalDate: prior.approvalDate || "",
    adoptionType: prior.adoptionType || "",
    adoptionDate: prior.adoptionDate || "",
    origAdoptionDate: prior.originalAdoptionDate || "",
    legalInPlace: prior.isLegalAuthorityCurrent ? "Yes" : "No"
  };
}

export const createEmptyEntry = (id) => ({
  id,
  subagency: "",
  accountType: "",
  active: "",
  chargeStatus: "",
  chargeCategory: "",
  serviceDesc: "",
  estParcels: "",
  estAmount: "",
  enablingStatute: "",
  legalAuthorityType: "",
  approvalMethod: "",
  electionDate: "",
  approvalDate: "",
  adoptionType: "",
  adoptionDate: "",
  origAdoptionDate: "",
  legalInPlace: "",
  reqDescUpdate: false,
  newDescription: ""
});
